/* The control registry: field type in, component out.
 *
 * This is the file the whole "nothing missing" claim rests on. 836 DocTypes
 * use 40 field types between them, so a control here is worth roughly twenty
 * screens somebody would otherwise hand-build — and a field type that is
 * missing from this map is a form that cannot be drawn at all.
 *
 * `coverage/tools/coverage.py` reads `fieldtypes.json` beside this file and
 * fails the build when a type is marked done there and missing here, so the
 * two cannot drift.
 */
import type { DocField } from '../types'
import type { Control, ControlProps } from './kit'
import { CodeControl, MarkdownControl, RichTextControl } from './richtext'
import {
  AutocompleteControl, ButtonControl, CheckControl, ColorControl, DataControl, DurationControl, PasswordControl, ReadOnlyControl, SelectControl, makeDate, makeNumber, makeTextArea,
} from './basic'
import {
  BarcodeControl, GeolocationControl, HtmlControl, IconControl, ImageControl,
  SignatureControl, makeAttach, makeMultiSelect,
} from './files'
import { DynamicLinkControl, LinkControl } from './link'
import { makeTableControl } from './table'
import { Field } from './kit'

export type { Control, ControlProps } from './kit'
export { Field } from './kit'

/** Anything not yet built: says so plainly and shows the raw value, rather
 *  than rendering nothing and leaving a hole in the form. */
const NotBuiltYet: Control = (p) => (
  <Field field={p.field} required={p.required} error={p.error} bare={p.bare}>
    <div className="input" style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--sunk)' }}>
      <span className="chip chip-warn">{p.field.fieldtype}</span>
      <span className="truncate small muted">{p.value ? String(p.value) : 'Not editable here yet'}</span>
    </div>
  </Field>
)

export function buildRegistry(childFields: (doctype: string) => DocField[]): Record<string, Control> {
  const table = makeTableControl((props: ControlProps) => renderWith(registry, props), childFields)
  const registry: Record<string, Control> = {
    // text
    Data: DataControl,
    'Small Text': makeTextArea(2),
    Text: makeTextArea(3),
    'Long Text': makeTextArea(6),
    Code: CodeControl,
    JSON: CodeControl,
    'Text Editor': RichTextControl,
    'HTML Editor': CodeControl,
    'Markdown Editor': MarkdownControl,
    Password: PasswordControl,
    'Read Only': ReadOnlyControl,
    // numbers
    Int: makeNumber('Int'),
    Float: makeNumber('Float'),
    Currency: makeNumber('Currency'),
    Percent: makeNumber('Percent'),
    Duration: DurationControl,
    // choices
    Check: CheckControl,
    Select: SelectControl,
    Autocomplete: AutocompleteControl,
    Color: ColorControl,
    // relations
    Link: LinkControl,
    'Dynamic Link': DynamicLinkControl,
    Table: table,
    // time
    Date: makeDate('Date'),
    Datetime: makeDate('Datetime'),
    Time: makeDate('Time'),
    'Table MultiSelect': makeMultiSelect(),
    // files
    Attach: makeAttach('file'),
    'Attach Image': makeAttach('image'),
    Image: ImageControl,
    Signature: SignatureControl,
    // the last few, one or two forms each
    Barcode: BarcodeControl,
    Geolocation: GeolocationControl,
    Icon: IconControl,
    // display-only markup the DocType carries with it
    HTML: HtmlControl,
    // actions
    Button: ButtonControl,
  }
  return registry
}

/**
 * Render one field.
 *
 * It returns an **element**, never the result of calling the control as a
 * function. Calling it would make its hooks belong to whichever component is
 * drawing the form, so the moment a `depends_on` changed which fields are
 * visible, React saw a different number of hooks and threw "Rendered more
 * hooks than during the previous render" — which is exactly what happened the
 * first time this form was opened in a browser, on 23 Sep 2026.
 *
 * The key is the fieldname: a control keeps its own state (what you are
 * typing into a number box, an open Link dropdown) and that state must follow
 * the field, not the position it happens to occupy this render.
 */
export function renderWith(registry: Record<string, Control>, props: ControlProps) {
  const Chosen = registry[props.field.fieldtype] ?? NotBuiltYet
  return <Chosen key={props.field.fieldname} {...props} />
}

/** Which types have a control. The coverage checker reads the registry
 *  through this, so "done" in fieldtypes.json can be verified rather than
 *  believed. */
export function builtFieldTypes(): string[] {
  return Object.keys(buildRegistry(() => []))
}
